import { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 63);

const Onboarding = () => {
  const { user, loading, tenantSlug } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingSlug, setExistingSlug] = useState<string | null | undefined>(undefined);

  // If the auth hook isn't active yet, check tenant_members directly for an existing org
  useEffect(() => {
    if (!user || tenantSlug) { setExistingSlug(null); return; }
    supabase
      .from('tenant_members')
      .select('tenants(slug)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .single()
      .then(({ data }) => {
        const s = (data?.tenants as { slug?: string } | null)?.slug;
        setExistingSlug(s ?? null);
      });
  }, [user, tenantSlug]);

  const checkSlug = useCallback(async (value: string) => {
    if (!value || value.length < 3) {
      setSlugAvailable(null);
      return;
    }
    setCheckingSlug(true);
    const { data, error } = await supabase.rpc('check_slug_available', { p_slug: value });
    // If the RPC errors (e.g. function not yet deployed), treat as unknown, not taken
    setSlugAvailable(error ? null : (data ?? false));
    setCheckingSlug(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => checkSlug(slug), 400);
    return () => clearTimeout(timer);
  }, [slug, checkSlug]);

  // Already has a tenant via JWT claim — skip to the app
  if (!loading && tenantSlug) {
    return <Navigate to={`/${tenantSlug}/app`} replace />;
  }

  // Auth hook not yet active but user already has a tenant membership in DB
  if (!loading && existingSlug) {
    return <Navigate to={`/${existingSlug}/app`} replace />;
  }

  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }

  if (loading || existingSlug === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleOrgNameChange = (value: string) => {
    setOrgName(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(slugify(value));
  };

  const handleSubmit = async () => {
    if (!orgName.trim()) {
      toast({ title: 'Required', description: 'Please enter an organization name.', variant: 'destructive' });
      return;
    }
    if (!slug || slug.length < 3) {
      toast({ title: 'Invalid slug', description: 'Slug must be at least 3 characters.', variant: 'destructive' });
      return;
    }
    if (slugAvailable === false) {
      toast({ title: 'Slug taken', description: 'Please choose a different URL slug.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc('create_tenant', {
        p_name: orgName.trim(),
        p_slug: slug,
      });

      if (error) throw error;

      // Refresh session so the JWT now contains the new tenant claims
      await supabase.auth.refreshSession();

      toast({ title: 'Organisation created!', description: `Welcome to ${orgName}` });
      navigate(`/${slug}/app`, { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create organisation';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const slugStatusIcon = () => {
    if (checkingSlug) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (slugAvailable === true) return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (slugAvailable === false) return <XCircle className="h-4 w-4 text-destructive" />;
    return null;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Set up your organisation</CardTitle>
          <CardDescription>
            Create your workspace. You can invite teammates after setup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Organisation name</Label>
            <Input
              id="org-name"
              placeholder="Acme Corp"
              value={orgName}
              onChange={(e) => handleOrgNameChange(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL slug</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  id="slug"
                  placeholder="acme-corp"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  disabled={isSubmitting}
                  className="pe-8"
                />
                <span className="absolute end-2 top-1/2 -translate-y-1/2">
                  {slugStatusIcon()}
                </span>
              </div>
            </div>
            {slug && (
              <p className="text-xs text-muted-foreground">
                Your app will be at <span className="font-medium">/{slug}/app</span>
              </p>
            )}
            {slugAvailable === false && (
              <p className="text-xs text-destructive">This slug is already taken.</p>
            )}
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || slugAvailable === false || !orgName.trim() || !slug || slug.length < 3}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                Creating…
              </>
            ) : (
              'Create organisation'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
